// components/SkillsExpertise/SkillExpertise.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Avatar,
  LinearProgress,
  Divider,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Domain as DomainIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SkillExpertise = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState(null);

  // Import Excel States
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);

  // Form states
  const [formData, setFormData] = useState({
    skill_name: '',
    proficiency: 3,
    years_of_experience: 0,
    skill_category: 'Secondary',
    domain_name: '',
    domain_years: 0
  });

  useEffect(() => {
    fetchSkills();
    fetchSummary();
  }, []);

  // API Calls
  const fetchSkills = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/skill-expertise`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSkills(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch skills');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/skill-expertise/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSummary(response.data.data);
    } catch (err) {
      //console.error('Failed to fetch summary:', err);
    }
  };

  const createSkill = async (data) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/skill-expertise`, data, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSuccess('Skill added successfully');
      fetchSkills();
      fetchSummary();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create skill');
      return false;
    }
  };

  const updateSkill = async (id, data) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/skill-expertise/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSuccess('Skill updated successfully');
      fetchSkills();
      fetchSummary();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update skill');
      return false;
    }
  };

  const deleteSkill = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/skill-expertise/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSuccess('Skill deleted successfully');
      fetchSkills();
      fetchSummary();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete skill');
      return false;
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/skill-expertise/migration/template`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          withCredentials: true
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employee_skills_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      //console.error('Error downloading template:', err);
      setError('Failed to download template');
    }
  };

  const handleUploadExcel = async () => {
    if (!importFile) {
      setError('Please select an Excel file to upload');
      return;
    }
    setImporting(true);
    setImportErrors([]);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API_BASE_URL}/api/skill-expertise/migration/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );
      
      if (response.data.status === 'success') {
        setSuccess(response.data.message || 'Skills imported successfully!');
        setOpenImportDialog(false);
        setImportFile(null);
        fetchSkills();
        fetchSummary();
      }
    } catch (err) {
      //console.error('Error importing Excel:', err);
      if (err.response?.data?.errors) {
        setImportErrors(err.response.data.errors);
        setError('Excel validation failed. Please fix errors and retry.');
      } else {
        setError(err.response?.data?.message || 'Failed to import employee skills');
      }
    } finally {
      setImporting(false);
    }
  };

  // Handlers
  const handleAddSkill = () => {
    setSelectedSkill(null);
    setFormData({
      skill_name: '',
      proficiency: 3,
      years_of_experience: 0,
      skill_category: 'Secondary',
      domain_name: '',
      domain_years: 0
    });
    setOpenDialog(true);
  };

  const handleEditSkill = (skill) => {
    setSelectedSkill(skill);
    setFormData({
      skill_name: skill.skill_name,
      proficiency: skill.proficiency,
      years_of_experience: skill.years_of_experience,
      skill_category: skill.skill_category,
      domain_name: skill.domain_name,
      domain_years: skill.domain_years
    });
    setOpenDialog(true);
  };

  const handleDeleteSkill = async (id) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) return;
    await deleteSkill(id);
  };

  const handleSubmit = async () => {
    if (!formData.skill_name || !formData.domain_name) {
      setError('Skill name and domain name are required');
      return;
    }

    let success = false;
    if (selectedSkill) {
      success = await updateSkill(selectedSkill.id, formData);
    } else {
      success = await createSkill(formData);
    }

    if (success) {
      setOpenDialog(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'proficiency' || name === 'years_of_experience' || name === 'domain_years' 
        ? parseFloat(value) 
        : value
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filter logic
  const getFilteredSkills = () => {
    let filtered = skills;
    
    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(skill => skill.skill_category === filterCategory);
    }

    // Tab filter
    if (tabValue === 1) {
      filtered = filtered.filter(skill => skill.skill_category === 'Primary');
    } else if (tabValue === 2) {
      filtered = filtered.filter(skill => skill.skill_category === 'Secondary');
    }

    // Search
    if (searchTerm) {
      filtered = filtered.filter(skill => 
        skill.skill_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        skill.domain_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (skill.personal && (
          (skill.personal.firstName && skill.personal.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (skill.personal.lastName && skill.personal.lastName.toLowerCase().includes(searchTerm.toLowerCase()))
        ))
      );
    }

    return filtered;
  };

  const getProficiencyColor = (level) => {
    const colors = {
      1: '#ff6b6b',
      2: '#ffa94d',
      3: '#ffd93d',
      4: '#6bcb77',
      5: '#4d96ff'
    };
    return colors[level] || '#808080';
  };

  const getProficiencyLabel = (level) => {
    const labels = {
      1: 'Beginner',
      2: 'Novice',
      3: 'Intermediate',
      4: 'Advanced',
      5: 'Expert'
    };
    return labels[level] || 'Unknown';
  };

  const filteredSkills = getFilteredSkills();
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Skills & Expertise Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage employee skills, proficiencies, and domain expertise
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchSkills(); fetchSummary(); }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadFileIcon />}
            onClick={() => setOpenImportDialog(true)}
          >
            Import Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSkill}
          >
            Add Skill
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Skills
                </Typography>
                <Typography variant="h4">{summary.totalSkills}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: 'primary.main' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Primary Skills
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {summary.primarySkills}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: 'grey.500' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Secondary Skills
                </Typography>
                <Typography variant="h4" color="grey.700">
                  {summary.secondarySkills}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: 'success.main' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Top Skills
                </Typography>
                <Typography variant="h4" color="success.main">
                  {summary.topSkills?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by skill, domain, or employee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="Primary">Primary</MenuItem>
                <MenuItem value="Secondary">Secondary</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography variant="body2" color="text.secondary" align="right">
              {filteredSkills.length} skills found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}  variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile>
          <Tab label="All Skills" />
          <Tab label="Primary Skills" />
          <Tab label="Secondary Skills" />
          <Tab label="By Domain" />
        </Tabs>
      </Box>

      {/* Skills Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Skill Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Proficiency</TableCell>
              <TableCell>Experience</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Domain Exp.</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredSkills.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No skills found. Click "Add Skill" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSkills.map((skill) => (
                <TableRow key={skill.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {skill.personal?.firstName?.charAt(0) || 'E'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {skill.personal ? `${skill.personal.firstName} ${skill.personal.lastName}` : 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {skill.personal?.roles?.designation || 'No designation'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {skill.skill_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={skill.skill_category}
                      color={skill.skill_category === 'Primary' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={skill.proficiency * 20}
                        sx={{
                          width: 80,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getProficiencyColor(skill.proficiency)
                          }
                        }}
                      />
                      <Typography variant="caption">
                        {getProficiencyLabel(skill.proficiency)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {skill.years_of_experience} yrs
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={skill.domain_name}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {skill.domain_years} yrs
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditSkill(skill)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteSkill(skill.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedSkill ? 'Edit Skill' : 'Add New Skill'}
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Skill Name"
              name="skill_name"
              value={formData.skill_name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Skill Category</InputLabel>
              <Select
                name="skill_category"
                value={formData.skill_category}
                onChange={handleInputChange}
                label="Skill Category"
              >
                <MenuItem value="Primary">Primary</MenuItem>
                <MenuItem value="Secondary">Secondary</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Proficiency Level</InputLabel>
              <Select
                name="proficiency"
                value={formData.proficiency}
                onChange={handleInputChange}
                label="Proficiency Level"
              >
                <MenuItem value={1}>1 - Beginner</MenuItem>
                <MenuItem value={2}>2 - Novice</MenuItem>
                <MenuItem value={3}>3 - Intermediate</MenuItem>
                <MenuItem value={4}>4 - Advanced</MenuItem>
                <MenuItem value={5}>5 - Expert</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Years of Experience"
              name="years_of_experience"
              type="number"
              value={formData.years_of_experience}
              onChange={handleInputChange}
              fullWidth
              inputProps={{ min: 0, max: 50, step: 0.5 }}
            />
            <TextField
              label="Domain Name"
              name="domain_name"
              value={formData.domain_name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Domain Experience (Years)"
              name="domain_years"
              type="number"
              value={formData.domain_years}
              onChange={handleInputChange}
              fullWidth
              inputProps={{ min: 0, max: 50, step: 0.5 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.skill_name || !formData.domain_name}
          >
            {selectedSkill ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Import Excel Skills Dialog */}
      <Dialog open={openImportDialog} onClose={() => { setOpenImportDialog(false); setImportFile(null); setImportErrors([]); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Import Employee Skills & Expertise</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel workbook containing employee skill details. Each row must be linked using the Employee ID.
            </Typography>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ alignSelf: 'flex-start' }}
            >
              Download Excel Template
            </Button>

            <Box sx={{ border: '2px dashed #CBD5E1', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#F8FAFC', cursor: 'pointer', mt: 1 }}>
              <input
                accept=".xlsx, .xls"
                style={{ display: 'none' }}
                id="skills-file-upload-input"
                type="file"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
              <label htmlFor="skills-file-upload-input" style={{ cursor: 'pointer' }}>
                <UploadFileIcon sx={{ fontSize: 40, color: '#6366F1', mb: 1 }} />
                <Typography variant="body2" fontWeight="bold">
                  {importFile ? importFile.name : 'Click to select Excel file (.xlsx)'}
                </Typography>
                {!importFile && (
                  <Typography variant="caption" color="text.secondary">
                    Supports 500+ employee records bulk load
                  </Typography>
                )}
              </label>
            </Box>

            {importErrors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  Validation Checklist Corrections:
                </Alert>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#FEF2F2' }}>
                      <TableRow>
                        <TableCell><strong>Row</strong></TableCell>
                        <TableCell><strong>Error Description</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importErrors.map((err, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ color: '#DC2626', fontWeight: 'bold' }}>{err.row}</TableCell>
                          <TableCell sx={{ color: '#7F1D1D' }}>{err.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenImportDialog(false); setImportFile(null); setImportErrors([]); }}>Cancel</Button>
          <Button
            onClick={handleUploadExcel}
            variant="contained"
            color="primary"
            disabled={!importFile || importing}
            startIcon={importing ? <CircularProgress size={20} /> : <StarIcon />}
          >
            {importing ? 'Importing...' : 'Upload & Process'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SkillExpertise;